/**
 * A drag-reorderable managed leaf header cell for the v8 {@link DataTable}.
 *
 * Renders a semantic `<th>` inside the Tailwind-only engine:
 *   - The whole cell is the dnd-kit sortable node — its rect drives the
 *     `horizontalListSortingStrategy` push-aside layout — while ONLY the grip handle carries the
 *     drag `listeners` / `attributes` (and the grab cursor). The grip is absolutely positioned so
 *     it OVERLAYS the header's left edge and fades in on hover without shifting the title. That
 *     split keeps the header interactions intact: the title and its caret are a button that sorts,
 *     the grip is the sole drag/reorder affordance, and the right-edge resize handle still resizes.
 *     The cell's Tab stops are the grip, the button, then the resize handle; a column that cannot be sorted
 *     has no button.
 *   - The dragged column is shown as an opaque, outlined clone floating in a dnd-kit
 *     `DragOverlay` (rendered by DataTable); here the original cells only DIM into a placeholder
 *     and ease to their sorted slot.
 *   - Drag state is broadcast to table-scoped CSS variables keyed by column id so the body cells
 *     follow WITHOUT a per-frame re-render: `columnDragXVar` (strategy shift), `columnDragTransitionVar`,
 *     `columnDragOpacityVar` (dim the dragged column into a placeholder while its opaque clone
 *     floats in the overlay) and `columnDropEdgeVar` (a thin grey insertion line on the column
 *     the pointer is over). `data-col-id` lets the overlay locate this column's cells to measure
 *     their heights, and is also the hook every body cell uses to find its column.
 *
 * Sticky / fixed / locked columns are NOT rendered through this component — they stay plain
 * `<th>`s (DataTable's default header), so they can be neither dragged nor displaced out of the
 * sticky prefix.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator as DragIndicatorIcon } from 'components/icons';
import { GLIDE } from 'layout/NewDesignLayout/Sidebar/usePinDrag';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { AriaAttributes, CSSProperties, ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { columnDragOpacityVar, columnDragTransitionVar, columnDragXVar, columnDropEdgeVar } from './cssVars';

/** Opacity the dragged column's original cells dim to while its opaque clone floats in the
 *  DragOverlay — i.e. the "where it came from" placeholder. */
const PLACEHOLDER_OPACITY = '0.4';

/**
 * Whether a dropped column lands, and the others make room, on the pins' spring (`GLIDE`): in the new design, with
 * motion allowed. Classic and reduced motion keep dnd-kit's 200ms `ease`. Read off the document, as the engine runs
 * without the store `useIsNewDesign` reads.
 */
export const reorderOnSpring = () =>
    document.documentElement.dataset.design === 'new' &&
    !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

/** Thin grey insertion line marking where the dragged column will drop. A single edge
 *  (not a full outline) on the column the pointer is over, on the side it will arrive from. Its
 *  colour is the module's `--dt-drop-line`, which the scroll container declares for both designs. */
const DROP_LINE = 'var(--dt-drop-line)';
const DROP_EDGE_RIGHT = `inset -2px 0 0 0 ${DROP_LINE}`;
const DROP_EDGE_LEFT = `inset 2px 0 0 0 ${DROP_LINE}`;

/** Map a column's textAlign to flex justification — the header cell is a flex box, so `text-align`
 *  does not position its inner content. */
const justify = (textAlign: CSSProperties['textAlign']): CSSProperties['justifyContent'] =>
    textAlign === 'right' || textAlign === 'end' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start';

type Props = {
    columnId: string;
    /** The column's plain-text name, which the grip's own name carries. */
    label: string;
    /** The id of the rendered title, which alone names the cell. */
    titleId?: string;
    /** True when this is one of the leading sticky-prefix columns (drives the sticky CSS hooks).
     *  Note: a sticky column is non-draggable, so it never reaches this component — kept for shape
     *  parity with DataTable's plain header path, which DataTable passes through unchanged. */
    isSticky: boolean;
    /** True when this is the last sticky-prefix column (edge-shadow hook). */
    isLastSticky: boolean;
    /** Text alignment for the header content (mirrors meta.textAlign on the plain header). */
    textAlign?: CSSProperties['textAlign'];
    /** Whether the title is a button that sorts, and the handler its click runs. */
    canSort: boolean;
    onSortToggle?: (event: unknown) => void;
    /** Current sort state for the `aria-sort` attribute on the header cell (a11y). Omitted for
     *  non-sortable columns. */
    ariaSort?: AriaAttributes['aria-sort'];
    /** The rendered header title + sort-caret content (built by DataTable.renderHeaderCell so the
     *  markup lives in ONE place); the sort button's content when the column sorts. */
    children: ReactNode;
    /** Right-edge resize handle; rendered as a sibling of the title, NOT part of the handle. */
    resizeHandleSlot: ReactNode;
    /** The CSS-var NAME (e.g. `--utw-w-<id>`) this cell reads its width from, as
     *  `width: var(<name>)`. A dragged column is never sticky, so width + transform never collide. */
    widthVar: string;
};

const DataTableSortableHeaderCell = ({
    columnId,
    label,
    titleId,
    isSticky,
    isLastSticky,
    textAlign,
    canSort,
    onSortToggle,
    ariaSort,
    children,
    resizeHandleSlot,
    widthVar,
}: Props) => {
    const {
        setNodeRef,
        setActivatorNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
        isSorting,
        isOver,
        activeIndex,
        index,
        // Suppress dnd-kit's post-drop FLIP on the committed reorder. The dropped column eases into
        // its slot via the DragOverlay glide (REORDER_DROP_ANIMATION in DataTable); the default
        // layout animation would run a second, redundant transition on this header underneath that
        // glide — hidden by the glide's side-effect, but its transition (dnd-kit's 200ms, or
        // `GLIDE`) can outlast the glide and cause a small settle jump on reveal. The live drag
        // push is unaffected (the sorting transform).
    } = useSortable({
        id: columnId,
        animateLayoutChanges: () => false,
        transition: reorderOnSpring() ? GLIDE : undefined,
    });

    // The column the pointer is over (but not the dragged one) shows the insertion line, on the
    // edge the dragged column arrives from: its right when dragged from the left, else its left.
    const dropEdge =
        isSorting && isOver && !isDragging ? (activeIndex < index ? DROP_EDGE_RIGHT : DROP_EDGE_LEFT) : 'none';

    // Capture the cell node (composing dnd-kit's setNodeRef) so we can write the column's drag CSS
    // variables on the enclosing <table> — the nearest common ancestor of this header and the
    // column's body cells, so both read the same variables.
    const cellElRef = useRef<HTMLTableCellElement | null>(null);
    const setRefs = useCallback(
        (node: HTMLTableCellElement | null) => {
            cellElRef.current = node;
            setNodeRef(node);
        },
        [setNodeRef]
    );

    useLayoutEffect(() => {
        const table = cellElRef.current?.closest('table');
        if (!table) return;
        const dragXVar = columnDragXVar(columnId);
        const dragTransitionVar = columnDragTransitionVar(columnId);
        const dragOpacityVar = columnDragOpacityVar(columnId);
        const dropEdgeVar = columnDropEdgeVar(columnId);
        // Strategy shift: with the DragOverlay present, dnd-kit gives even the dragged column a
        // bounded "sorted slot" transform (not the unbounded pointer-follow), so this stays within
        // the table and never inflates scrollWidth. A null transform ⇒ reset to `none`.
        table.style.setProperty(dragXVar, transform ? `translateX(${Math.round(transform.x)}px)` : 'none');
        table.style.setProperty(dragTransitionVar, transform ? (transition ?? 'none') : 'none');
        // Dim the dragged column into a placeholder; its opaque clone is in the overlay.
        table.style.setProperty(dragOpacityVar, isDragging ? PLACEHOLDER_OPACITY : '1');
        // Thin insertion line on the drop-target column (else none).
        table.style.setProperty(dropEdgeVar, dropEdge);
        // Reset to idle on unmount (or before each re-run): these vars live on the SHARED <table>,
        // so a header that unmounts mid-drag or is swapped for a plain header would otherwise leave
        // stale transform/opacity/drop-edge values still shifting this column's body cells.
        return () => {
            table.style.setProperty(dragXVar, 'none');
            table.style.setProperty(dragTransitionVar, 'none');
            table.style.setProperty(dragOpacityVar, '1');
            table.style.setProperty(dropEdgeVar, 'none');
        };
    }, [columnId, transform, transition, isDragging, dropEdge]);

    const style: CSSProperties = {
        width: `var(${widthVar})`,
        // The cell is a flex box, so its inline-flex activator is positioned by justification, not
        // by text-align — right/center sortable headers would render left-aligned without this.
        justifyContent: justify(textAlign),
        // Geometry rides through inline style. The cell is already `position: sticky` for sticky
        // columns via the CSS module — but a draggable column is never sticky, so setting transform
        // here is safe and won't break pinning.
        // Translate only — NOT `CSS.Transform.toString`, which also emits the strategy's
        // scaleX/scaleY and would stretch the header text across variable-width columns.
        transform: CSS.Translate.toString(transform),
        transition,
        // NOTE: the placeholder dim lives on the inner content (below), NOT here. The <th> is
        // `position: sticky` for the header, so fading the whole cell would fade its opaque
        // background too and let scrolled body rows show through the pinned header. Keeping the
        // cell opaque (with its z-index above the body) and dimming only the content preserves the
        // "where it came from" ghost without the bleed.
        // Insertion line when this is the drop target.
        boxShadow: dropEdge !== 'none' ? dropEdge : undefined,
    };

    return (
        <th
            ref={setRefs}
            data-col-id={columnId}
            data-sticky={isSticky || undefined}
            data-sticky-last={isLastSticky || undefined}
            aria-sort={ariaSort}
            aria-labelledby={titleId}
            style={style}
        >
            <div
                className={cn(
                    'group/drag relative inline-flex max-w-full items-center gap-[9.2px] whitespace-nowrap select-none',
                    // Reveal the grip in a vacated gutter instead of on top of the title: on hover
                    // (or when the grip is keyboard-focused) the row eases in 28px of left padding
                    // (24px grip square + 4px gap). box-border + max-w-full cap the row at the cell,
                    // so that padding SQUEEZES the truncating title (see headerContent) rather than
                    // shoving the trailing sort caret out through the cell's overflow clip. Plain
                    // hover / has-[>span:focus-visible] (NOT group-* — this IS the group element, which
                    // group-* can't match). The grip is the row's only span child, so focus on the sort
                    // button leaves the title where it is. focus-visible (not focus-within) so a POINTER
                    // drag — after which dnd-kit restores focus to the grip — does NOT keep the title slid
                    // out (focus-within would, until you clicked elsewhere); it matches the grip's own
                    // focus-visible reveal, so only true keyboard focus opens the gutter.
                    // On touch the grip's 44px area needs a 10px gap to meet the title's sort button.
                    'box-border transition-[padding] duration-150 ease-out hover:pl-7 has-[>span:focus-visible]:pl-7 pointer-coarse:has-[>span:focus-visible]:pl-[34px]'
                )}
                // Dim ONLY the content into the placeholder ghost (the opaque clone floats in the
                // overlay); the <th> background stays opaque so the pinned header never shows the
                // scrolled body through it.
                style={{
                    ...(textAlign ? { textAlign } : {}),
                    opacity: isDragging ? Number(PLACEHOLDER_OPACITY) : undefined,
                }}
            >
                {/* Drag handle: the ONLY drag affordance — the dnd listeners, keyboard activation and
                    the grab cursor live here, not on the whole header. dnd-kit makes it a focusable
                    button; the label names which column it moves. Absolute at left-0, it fades in over
                    100ms on hover into the gutter the title vacates (the inner row eases in left
                    padding), so the grip never overlaps the title. The opaque rounded square (header
                    bg) covers the title only during the slide, and settles at full opacity so it never
                    turns translucent. */}
                <span
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    aria-label={t('table.move-column', { column: label })}
                    className={cn(
                        'hit-area absolute top-1/2 left-0 flex h-6 w-6 -translate-y-1/2 touch-none items-center justify-center rounded-xl bg-[var(--dt-head-bg)] opacity-0 transition-opacity duration-100',
                        'group-hover/drag:opacity-100 focus-visible:opacity-100',
                        // On touch nothing reveals it, so it takes no taps meant for the sort button under its area.
                        'pointer-coarse:pointer-events-none pointer-coarse:focus-visible:pointer-events-auto',
                        NEW_FOCUS_RING,
                        isDragging ? 'cursor-grabbing' : 'cursor-grab'
                    )}
                >
                    <DragIndicatorIcon className="text-secondary new:text-muted-foreground text-[18px]" />
                </span>
                {canSort ? (
                    // A real button, so Enter and Space sort as a click does; `aria-sort` on the <th>
                    // carries the state. min-w-0 lets the button shrink with the row, so the title in it truncates.
                    <button
                        type="button"
                        onClick={onSortToggle}
                        className={cn(
                            'inline-flex min-w-0 cursor-pointer items-center gap-[9.2px] rounded-sm',
                            NEW_FOCUS_RING
                        )}
                    >
                        {children}
                    </button>
                ) : (
                    children
                )}
            </div>
            {resizeHandleSlot}
        </th>
    );
};

export default DataTableSortableHeaderCell;
